import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import usePlacesAutocomplete, { getGeocode, getLatLng } from 'use-places-autocomplete';
import { translateWithI18Next } from '@kfc-global/kfc-i18n/lib';
import { NULL } from 'common/constants/SharedConstants';
import { ADDRESS_STORE_CONSTANTS, FORM_HEADING } from 'organisms/AccountPage/AU/Constants/Constants';
import { TRANSLATE_MAPPING_KEY } from 'common/constants/i18nTranslateKeys';
import { setAddressDetails } from 'organisms/ContactUs/Utils/contactUsUtils';
import { updateNewAddressHandler } from 'common/utilities/PaymentUtils';
import { BillingAddressTemplate } from './BillingAddressTemplate';
import geoLocation from '@kfc-global/react-shared/utils/geoLocation';
import PaymentSaveCardTemplate from './PaymentSaveCardTemplate';
import { isPickupDisposition } from 'common/utilities/utils';
import isEmpty from 'lodash/isEmpty';

const { INITIAL_STATE } = ADDRESS_STORE_CONSTANTS;
export const getInitialAddressState = props => (isEmpty(props?.address) ? INITIAL_STATE : props?.address);

const PaymentBillingAndSavingCardTemplate = props => {
  const { checkoutDetails, updateCheckoutDetails } = props;
  const { billingAddressAndSaveCard } = checkoutDetails;
  const [showResults, setShowResults] = useState(true);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const tenantReducer = useSelector(data => data.tenantReducer);
  const { storeSearch = {}, countryNameInDefaultLang } = tenantReducer;
  const [addresses, setSaveAddress] = useState(getInitialAddressState(props));
  const [error, setError] = useState(false);
  const geohashprecision = storeSearch?.geohashprecision;
  const boundingBoxSize = storeSearch?.boundingBoxSizeMeters / 1000;

  const { ready, value, suggestions, setValue, clearSuggestions } = usePlacesAutocomplete({
    requestOptions: {
      componentRestrictions: {
        country: process?.env?.REACT_APP_TENANT_CODE,
      },
      /* Define search scope here */
    },
    debounce: 300,
  });

  const biilingSuggestionRef = useRef(null);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutsideBillingAddress);
    return () => {
      document.removeEventListener('mousedown', handleClickOutsideBillingAddress);
    };
  });

  const handleClickOutsideBillingAddress = event => {
    biilingSuggestionRef.current && !biilingSuggestionRef.current.contains(event.target) && clearSuggestions();
  };

  const handleInputBilling = e => {
    setValue(e.target.value);
    const addressLines = [addresses?.addressLines[0], e.target.value];
    setSaveAddress({
      ...addresses,
      addressLines: addressLines,
      pinCode: e.target.value === '' ? '' : addresses.pinCode,
      state: e.target.value === '' ? '' : addresses.state,
      city: e.target.value === '' ? '' : addresses.city,
    });
    setShowSuggestions(true);
  };

  const handleChangeBilling = ({ target }) => {
    const { name, value } = target;
    name === FORM_HEADING.PIN_CODE && setSaveAddress({ ...addresses, [name]: value });
  };

  const handleSelect =
    ({ description, structured_formatting: { main_text, secondary_text } }) =>
    () => {
      // When user selects a place, we can replace the keyword without request data from API
      // by setting the second parameter to "false"
      // setValue(main_text, false);
      setShowSuggestions(false);
      setError(false);
      clearSuggestions();
      // dispatch(resetStores());
      // Get latitude and longitude via utility functions
      getGeocode({
        address: description,
        componentRestrictions: {
          country: process?.env?.REACT_APP_TENANT_CODE,
        },
      })
        .then(results => {
          setAddressDetails(
            NULL,
            results[0],
            setValue,
            setSaveAddress,
            clearSuggestions,
            checkFormValidationBillingformValidation,
            updateCheckoutDetails,
          );
          return getLatLng(results[0]);
        })
        .then(data => {
          geoLocation.calculateGH({
            lat: parseFloat(data?.lat),
            lon: parseFloat(data?.lng),
            distanceFromCurrentLoc: boundingBoxSize,
            geoHashPrecision: geohashprecision,
          });
          setSaveAddress(prevState => ({
            ...prevState,
            latitude: data?.lat,
            longitude: data?.lng,
          }));
        })
        .catch(error => {
          console.log('Error: ', error);
        });
    };

  const checkFormValidationBillingformValidation = () =>
    addresses?.addressLines[1]?.length &&
    addresses?.city?.length &&
    addresses?.pinCode?.length &&
    addresses?.state?.length;
  useEffect(() => {
    updateCheckoutDetails({
      enablePaymentProviderPlaceOrderBtn: true,
      paymentCardDetails: {
        ...checkoutDetails?.paymentCardDetails,
        isFormValid: true,
      },
      billingAddressAndSaveCard: {
        ...billingAddressAndSaveCard,
        addressline1: addresses?.addressLines?.[0],
        addressline2: addresses?.addressLines?.[1],
        fulladdress: `${addresses?.addressLines?.[0] + addresses?.addressLines?.[1]}`,
        city: addresses?.city,
        pincode: addresses?.pinCode,
        state: addresses?.state,
        location: {
          latitude: addresses?.location?.latitude || addresses?.latitude, //15.749100576992188,
          longitude: addresses?.location?.longitude || addresses?.longitude, //75.25417921693628
        },
        isBillingAddressFormDetailsValid: checkFormValidationBillingformValidation(),
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addresses]);

  return (
    <>
      <div className='savebilling-address'>
        {!isPickupDisposition(checkoutDetails?.service) && (
          <div
            className={` ${
              checkoutDetails?.isAddNewCard ? 'hidden' : 'd-flex'
            } form-checkbox-container form-checkbox-payonarrival`}
          >
            <input
              type='checkbox'
              className={`common_checkbox`}
              onChange={event =>
                updateNewAddressHandler({ event, checkoutDetails, updateCheckoutDetails, setShowResults })
              }
              data-testid={`Address-default-checkbox`}
              checked={showResults}
              name='cc-Payment-billing-address'
              id='Payment-billing-address'
              disabled={checkoutDetails?.service === 'pickup'}
              defaultChecked={checkoutDetails?.billingAddressAndSaveCard?.defaultAddressSelected}
            />
            <label title='title' className='form-check-label' for='Payment-billing-address'>
              {translateWithI18Next(TRANSLATE_MAPPING_KEY?.USE_DELIVARY_ADDRESS)}
            </label>
          </div>
        )}
        {(!showResults || isPickupDisposition(checkoutDetails?.service) || checkoutDetails?.isAddNewCard) && (
          <>
            <BillingAddressTemplate
              data-testid='billing-address'
              biilingSuggestionRef={biilingSuggestionRef}
              handleSelect={handleSelect}
              showSuggestions={showSuggestions}
              handleClickOutsideBillingAddress={handleClickOutsideBillingAddress}
              ready={ready}
              value={value}
              suggestions={suggestions}
              setValue={setValue}
              clearSuggestions={clearSuggestions}
              handleInputBilling={handleInputBilling}
              countryNameInDefaultLang={countryNameInDefaultLang}
              addresses={addresses}
              handleChangeBilling={handleChangeBilling}
              checkoutDetails={checkoutDetails}
            />
          </>
        )}
        {(checkoutDetails?.isAddNewCard || isPickupDisposition(checkoutDetails?.service) || !showResults) && (
          <div className='required-suggestion'>{translateWithI18Next(TRANSLATE_MAPPING_KEY?.MANDATORY_TEXT)}</div>
        )}
        {(checkoutDetails?.userParams?.loggedIn || checkoutDetails?.isAddNewCard) && (
          <PaymentSaveCardTemplate {...props} />
        )}
      </div>
    </>
  );
};

export default PaymentBillingAndSavingCardTemplate;
